import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Package, 
  Truck, 
  MapPin,
  ArrowRight,
  Phone
} from 'lucide-react';
import api, { getImageUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import GuestAccountCreation from '../components/GuestAccountCreation';
import TEXT_CONSTANTS from '../constants/text';
import { MetaPixelService } from '../services/metaPixel';

interface Order {
  _id: string;
  orderNumber: string;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: string;
  orderStatus: string;
  shippingAddress: {
    name: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
  };
  guestInfo?: {
    name: string;
    email: string;
    phone: string;
  };
  items: Array<{
    product: {
      _id: string;
      name: string;
      images: string[];
      price: number;
    };
    quantity: number;
    price: number;
  }>;
  createdAt: string;
  estimatedDelivery: string;
}

const OrderSuccess: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAccountCreation, setShowAccountCreation] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('Order ID not found');
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
        
        // Track purchase event
        const contentIds = response.data.items.map((item: any) => item.product._id);
        MetaPixelService.trackPurchase(
          response.data.totalAmount,
          'INR',
          contentIds
        );
        
        // Show account creation for guest orders
        if (!isAuthenticated && response.data?.guestInfo) {
          setShowAccountCreation(true);
        }
      } catch (error: any) {
        console.error('Error fetching order:', error);
        setError(error.response?.data?.message || 'Failed to fetch order details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, isAuthenticated]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEstimatedDelivery = () => {
    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + 7); // 7 days from now
    return deliveryDate.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-8">
            <div className="text-red-500 mb-4">
              <Package className="h-16 w-16 mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Order Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'We couldn\'t find the order you\'re looking for.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                       text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {TEXT_CONSTANTS.ORDERS.ORDER_SUCCESS}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {TEXT_CONSTANTS.MISC.THANK_YOU} for your purchase. Your order has been confirmed.
          </p>
        </div>

        {/* Guest Account Creation */}
        {showAccountCreation && !accountCreated && order?.guestInfo && (
          <div className="mb-8">
            <GuestAccountCreation
              guestEmail={order.guestInfo.email}
              guestName={order.guestInfo.name}
              onAccountCreated={() => {
                setAccountCreated(true);
                setShowAccountCreation(false);
              }}
              onSkip={() => setShowAccountCreation(false)}
            />
          </div>
        )}

        {/* Account Created Success */}
        {accountCreated && (
          <div className="mb-8">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-green-900 dark:text-green-100">
                    {TEXT_CONSTANTS.AUTH.ACCOUNT_CREATED}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    You can now login to track your orders and enjoy faster checkout.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Summary */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Order Summary
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Order Number</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">#{order._id.slice(-8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Order Date</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{order.paymentMethod}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
                  <p className="font-semibold text-primary-600 dark:text-primary-400">₹{order.totalAmount.toLocaleString()}</p>
                </div>
              </div>

              {/* Items */}
              <div className="border-t border-gray-200 dark:border-secondary-600 pt-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Items Ordered</h3>
                <div className="space-y-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <img
                        src={getImageUrl(item.product.images[0])}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{item.product.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Qty: {item.quantity} × ₹{item.price.toLocaleString()}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        ₹{(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Shipping Address
              </h2>
              <div className="text-gray-700 dark:text-gray-300">
                <p className="font-medium">{order.shippingAddress.name}</p>
                <p>{order.shippingAddress.street}</p>
                <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
                <p className="flex items-center mt-2">
                  <Phone className="h-4 w-4 mr-2" />
                  {order.shippingAddress.phone}
                </p>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Order Status */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Order Status
              </h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Order Confirmed</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(order.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-300 dark:bg-secondary-600 rounded-full mr-3"></div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Processing</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">We're preparing your order</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-300 dark:bg-secondary-600 rounded-full mr-3"></div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Shipped</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">On the way</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-300 dark:bg-secondary-600 rounded-full mr-3"></div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Delivered</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Expected by {getEstimatedDelivery()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                What's Next?
              </h2>
              <div className="space-y-3">
                <Link
                  to="/orders"
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-secondary-600 rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                >
                  <div className="flex items-center">
                    <Package className="h-5 w-5 mr-3 text-primary-600 dark:text-primary-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Track Your Orders</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </Link>
                <Link
                  to="/products"
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-secondary-600 rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                >
                  <div className="flex items-center">
                    <Truck className="h-5 w-5 mr-3 text-green-600" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Continue Shopping</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </Link>
              </div>
            </div>

            {/* Support */}
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-6">
              <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">
                Need Help?
              </h3>
              <p className="text-sm text-primary-700 dark:text-primary-300 mb-3">
                Our customer support team is here to help you with any questions.
              </p>
              <Link
                to="/contact"
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
              >
                Contact Support →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;